import React, { useEffect, useRef, useCallback } from "react";
import { COS_URL } from "../../const/constants.js";
import Header from "./Header/index.jsx";
import GeneralBasicOCR from "../features/GeneralBasicOCR.jsx";
import OcrUpload from "../ui/Upload.jsx";
import MultiUploadDropzone, { buildBatchImage } from "../ui/MultiUpload.jsx";

import ThumbnailPreview from "../ui/ThumbnailPreview.jsx";
import ImageOptimize from "../features/ImageOptimize";
import { Thumbnail } from "../ui/universe";
import { getRectPosition, customRound } from "../../utils/index";
import useSmoothScroll from "react-smooth-scroll-hook";
import Sider from "./Sider/index.jsx";
import { useMenuStore, useImageStore, useUIStore } from "../../store";
import "../../style/layout.css";

const imgFileType = ["jpg", "jpeg", "png", "bmp", "JPG", "JPEG", "PNG", "BMP"];

function OcrLayout({ onChangeAction }) {
  // 从 menuStore 获取状态（使用选择器确保正确订阅）
  const currentMenu = useMenuStore((state) => state.currentMenu);

  // 从 imageStore 获取状态（使用选择器确保正确订阅）
  const imgList = useImageStore((state) => state.imgList);
  const uploadImgList = useImageStore((state) => state.uploadImgList);
  const multiUploadPages = useImageStore((state) => state.multiUploadPages);
  const imgListType = useImageStore((state) => state.imgListType);
  const currentImage = useImageStore((state) => state.currentImage);
  const range = useImageStore((state) => state.range);
  const imgPreType = useImageStore((state) => state.imgPreType);
  const rectDocuments = useImageStore((state) => state.rectDocuments);
  // 从 imageStore 获取操作函数
  const {
    setImgList,
    setImgListType,
    setCurrentImage,
    setAngle,
    setRange,
    setImgPreType,
    setShowImgList,
    setRects,
    setRectDocuments,
    setSelectRectKey,
    setClipImg,
    setTotalPage,
    setPrefix,
    setIsImageError,
    clearImageState,
    addUploadImage,
    removeUploadImage,
    clearUploadImgList,
    clearMultiUploadPages,
  } = useImageStore();

  // 从 uiStore 获取状态（使用选择器确保正确订阅）
  const isLoading = useUIStore((state) => state.isLoading);
  const setIsLoading = useUIStore((state) => state.setIsLoading);

  // 本地状态 - 仅保留需要 ref 或临时状态
  const [result, setResult] = React.useState(null);
  const [showThumListBtn, setShowThumListBtn] = React.useState(false);

  const uploadRef = useRef();
  const imgPreRef = useRef();
  const GeneralBasicOCRRef = useRef();
  const domRef = useRef(document.body);
  const thumbnailRef = useRef();

  const { scrollTo } = useSmoothScroll({
    ref: domRef,
    speed: 100,
    direction: "x",
  });

  // 稳定回调引用，避免 ThumbnailPreview useEffect 依赖变化导致无限循环
  const handlePreviewSelect = useCallback(
    (val) => {
      setSelectRectKey(val);
    },
    [setSelectRectKey],
  );

  const handlePreviewClip = useCallback(
    (val) => {
      setClipImg(val);
    },
    [setClipImg],
  );

  // 切换图片/项目时，清空rect
  useEffect(() => {
    if (!currentMenu) return;
    clearImageState();
  }, [currentMenu, currentImage?.url]);

  // 切换项目时，清空上传列表
  useEffect(() => {
    clearUploadImgList();
    clearMultiUploadPages();
    setImgListType("default");
  }, [currentMenu]);

  // 多图上传：批次内容变化时把整批推给预览，否则识别结果返回前预览还停在示例图。
  // 注意一次选择的多张图是「逐个文件」进入批次的（1 张 → 2 张 → 3 张），
  // 所以不能只推一次，要用批次签名判断「这一批的页集合是否已推过」。
  // PDF 批次等「前端本地逐页渲染」完成后，用渲染出的页图作为预览底图
  const shownMultiPreviewKeyRef = useRef("");
  const pdfPreviewPages = useImageStore((state) => state.pdfPreviewPages);
  useEffect(() => {
    if (multiUploadPages.length === 0) {
      shownMultiPreviewKeyRef.current = "";
      return;
    }

    const batchSign = multiUploadPages
      .map((p) => p.uid || p.url || p.previewUrl)
      .join("|");
    const first = multiUploadPages[0];
    const isPdfBatch = (first?.fileType || "").toLowerCase() === "pdf";

    if (!isPdfBatch) {
      // 多张图片要整批进预览：只推第一张就会出现「上传多张图片，显示区只展示一张」
      // 同时剔除 pdf 页，避免非图片地址被当成图片渲染成空白页
      const previewKey = `img:${batchSign}`;
      if (shownMultiPreviewKeyRef.current === previewKey) return;

      const imagePages = multiUploadPages
        .filter((p) => (p.fileType || "").toLowerCase() !== "pdf")
        .map((p, idx) => ({
          ImageUrl: p.previewUrl || p.url,
          pageNum: idx + 1,
        }));
      if (imagePages.length === 0) return;

      imgPreRef?.current?.onPreChange({
        curImg: imagePages[0].ImageUrl,
        curpPdfList: imagePages,
        imgSize: null,
      });
      shownMultiPreviewKeyRef.current = previewKey;
      return;
    }

    // pdf：本地渲染完成即可预览，不等后端图片压缩包（对方 COS 不支持跨域）
    if (pdfPreviewPages.length > 0) {
      const previewKey = `pdf:${pdfPreviewPages.length}:${batchSign}`;
      if (shownMultiPreviewKeyRef.current === previewKey) return;

      imgPreRef?.current?.onPreChange({
        curImg: pdfPreviewPages[0].ImageUrl,
        curpPdfList: pdfPreviewPages,
        imgSize: {
          width: pdfPreviewPages[0].Width,
          height: pdfPreviewPages[0].Height,
        },
        perPageSize: pdfPreviewPages.length > 1,
      });
      shownMultiPreviewKeyRef.current = previewKey;
    }
  }, [multiUploadPages, pdfPreviewPages]);

  // rectDocuments 变化时更新 rects
  useEffect(() => {
    if (rectDocuments.length > 0) {
      const _rects = rectDocuments.map((r) => ({
        ...r,
        visible: true,
      }));
      setRects(_rects);
    } else {
      setRects([]);
    }
  }, [rectDocuments]);

  // currentMenu 变化时设置配置
  // 说明：setConfig 内部对「用户已有上传」做了保护，
  // 避免菜单对象引用变化时把已上传的图片/PDF 覆盖成示例图
  useEffect(() => {
    if (currentMenu) {
      setConfig(currentMenu);
    }
  }, [currentMenu]);

  const setConfig = (_result) => {
    if (!_result) return;
    const _prefix = _result.prefix || "Image";
    setPrefix(_prefix);
    const _imgList = _result?.imglist?.map((i, index) => {
      if (typeof i === "string") {
        return {
          previewUrl: i.indexOf("https") > -1 ? i : COS_URL + i,
          url: i.indexOf("https") > -1 ? i : COS_URL + i,
          OriginUrl: i.indexOf("https") > -1 ? i : COS_URL + i,
          cantPreview: _result?.fakeImglist?.length > 0,
          type: "url",
          isExample: true,
          pdfImgList: [],
          fileType: i?.split("?")[0].split(".").pop().toLowerCase(),
          prefix: _result?.ImageParams ? _result?.ImageParams : `${_prefix}Url`,
          param: _result?.fakeImglist
            ? i.indexOf("https") > -1
              ? _result?.fakeImglist[index]
              : COS_URL + _result?.fakeImglist[index]
            : "",
        };
      } else {
        return {
          previewUrl: i.url.indexOf("https") > -1 ? i.url : COS_URL + i.url,
          url: i.url.indexOf("https") > -1 ? i.url : COS_URL + i.url,
          defaultConfig: i.defaultConfig,
          OriginUrl: i.url.indexOf("https") > -1 ? i.url : COS_URL + i.url,
          cantPreview: _result?.fakeImglist?.length > 0,
          type: "url",
          isExample: true,
          pdfImgList: [],
          fileType: i.url.split("?")[0].split(".").pop().toLowerCase(),
          prefix: _result?.ImageParams ? _result?.ImageParams : `${_prefix}Url`,
          // 非图片文件（如 pdf）的首图预览，仅用于缩略图展示，不参与接口请求
          previewImg: i.previewImg
            ? i.previewImg.indexOf("https") > -1
              ? i.previewImg
              : COS_URL + i.previewImg
            : "",
          param: _result?.fakeImglist
            ? i.indexOf("https") > -1
              ? _result?.fakeImglist[index]
              : COS_URL + _result?.fakeImglist[index]
            : "",
        };
      }
    });
    setImgList(_imgList);

    // 用户已经有上传内容（图片或 PDF 批次）时，示例图只更新左上的示例列表，
    // 不抢占当前预览、不自动发起示例识别，避免把上传结果覆盖掉
    const {
      uploadImgList: _uploaded,
      multiUploadPages: _multiPages,
    } = useImageStore.getState();
    if (_uploaded?.length > 0 || _multiPages?.length > 0) {
      return;
    }

    setCurrentImage(_imgList?.[0]);
    setTotalPage(1);
    setResult(_imgList?.[0]);
    setImgPreType("preview");

    // 预览由 onPreChange 命令式驱动，不会跟随 currentImage 自动更新，
    // 这里必须主动切一次，否则切换菜单后会残留上一个菜单的图片
    // PDF 要等识别结果带回分页图，这里不主动切
    if (_imgList?.[0] && _imgList[0].fileType !== "pdf") {
      imgPreRef?.current?.onPreChange({
        curImg: _imgList[0].previewUrl || _imgList[0].url,
        curpPdfList: _imgList[0].pdfImgList || [],
        imgSize: null,
      });
    }

    // 处理图片显示
    if (domRef?.current?.clientWidth < _imgList?.length * 94) {
      setShowThumListBtn(true);
    } else {
      setShowThumListBtn(false);
    }

    // 首次加载时触发识别
    if (_imgList?.[0]) {
      setTimeout(() => {
        GeneralBasicOCRRef?.current?.start({
          image: _imgList[0],
          config: _imgList[0].defaultConfig,
        });
      }, 0);
    }
  };

  const handleImageClick = (img, isUpload = false) => {
    // 切换到「不同」图片时一律放行，即使上一张还在识别/轮询中：
    // 由 GeneralBasicOCR.start → handleStart 内部 handleStop()（停旧轮询）
    // + flowId++（作废旧流程事件）+ 清 loading 来关闭前一个流程。
    // 仅当重复点击「当前已激活」的同一张图且正在 loading 时才忽略，避免重复请求。
    const liveCurrent = useImageStore.getState().currentImage;
    const isSameImage = Boolean(
      img?.url &&
        (img.url === liveCurrent?.url ||
          (img.OriginUrl && img.OriginUrl === liveCurrent?.OriginUrl))
    );
    console.warn('[切题调试] handleImageClick', {
      imageUrl: img?.url,
      fileType: img?.fileType,
      isUpload,
      isLoading,
      isSameImage,
      willBlock: isLoading && isSameImage,
      isThinking: useUIStore.getState().isThinking,
    });
    if (isLoading && isSameImage) return;
    setCurrentImage(img);
    setTotalPage(img.pdfImgList?.length || 1);
    setResult(img);
    setImgPreType("preview");
    // 先切到这张图并同步预览，避免识别结果返回前大图仍停留在上一张。
    // PDF 批次若已带本地渲染的分页图（pdfImgList），立即切过去，不必等识别结果；
    // 只有确实没有任何分页图时才保持原样等结果（避免误把上一张图当成本批展示）
    const previewPages = img.pdfImgList || [];
    const hasPreviewPages = previewPages.length > 0;
    if (img.fileType !== "pdf" || hasPreviewPages) {
      const firstPage = previewPages[0];
      imgPreRef?.current?.onPreChange({
        curImg: hasPreviewPages
          ? firstPage.ImageUrl || firstPage.previewUrl
          : img.previewUrl || img.url,
        curpPdfList: previewPages,
        imgSize:
          hasPreviewPages && firstPage.Width
            ? { width: firstPage.Width, height: firstPage.Height }
            : null,
      });
    }
    GeneralBasicOCRRef?.current?.start({
      image: img,
      config: isUpload ? undefined : img.defaultConfig,
    });
  };

  const handleDeleteUploadImage = (e, index) => {
    e.stopPropagation();
    removeUploadImage(index);
  };

  const isMultiUpload = !!currentMenu?.multiUpload;

  // 历史批次：多图模式下「一次上传 = 一次入参」，上一批归档在 uploadImgList 里，
  // 这里把带页信息(isMultiUpload + multiPages)的记录挑出来单独展示
  const archivedBatches = (uploadImgList || [])
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item?.isMultiUpload && item?.multiPages?.length > 0);

  const handleBatchDelete = (e, index) => {
    e.stopPropagation();
    removeUploadImage(index);
  };

  // 当前批次也统一展示为「照片组」卡片（与历史批次形态一致）
  const currentBatchFirstPage = multiUploadPages[0];
  const currentBatchPreviewImg =
    currentBatchFirstPage &&
    (currentBatchFirstPage.fileType || "").toLowerCase() === "pdf"
      ? pdfPreviewPages[0]?.ImageUrl || ""
      : currentBatchFirstPage?.previewUrl || currentBatchFirstPage?.url;
  const isCurrentBatchActive =
    !!currentImage?.isMultiUpload &&
    !!currentImage?.multiPages?.[0]?.uid &&
    currentImage.multiPages[0].uid === currentBatchFirstPage?.uid;

  const handleCurrentBatchClick = () => {
    const cur = useImageStore.getState().currentImage;
    const isSameBatch =
      cur?.multiPages?.[0]?.uid &&
      cur.multiPages[0].uid === currentBatchFirstPage?.uid;
    const batchImage = buildBatchImage(
      multiUploadPages,
      isSameBatch ? cur : null
    );
    if (!batchImage) return;
    // pdf 批次：把本地渲染出的页图补进去（否则预览没有底图）
    if (batchImage.pdfImgList.length === 0 && pdfPreviewPages.length > 0) {
      batchImage.pdfImgList = pdfPreviewPages;
    }
    handleImageClick(batchImage, true);
  };

  return (
    <div className="pg-recognize">
      <Header onChangeAction={onChangeAction} />
      <div className="pg-layout">
        <Sider />
        <div
          className="auto-flex"
          style={{ background: "#F4F5F7", height: "100%", flex: 1 }}
        >
          <div className="preview_container">
            <>
              {imgPreType === "preview" ? (
                <ThumbnailPreview
                  cRef={imgPreRef}
                  style={{ background: "#fff" }}
                  onUpload={() => {
                    uploadRef.current.onUpload();
                  }}
                  onSelect={handlePreviewSelect}
                  onClip={handlePreviewClip}
                  imageList={
                    currentImage?.pdfImgList?.length > 0
                      ? currentImage.pdfImgList
                      : []
                  }
                  imageUrl={currentImage?.previewUrl || currentImage?.url}
                />
              ) : (
                <>
                  <ImageOptimize
                    type="split"
                    defaultRange={range}
                    getRange={(e) => setRange(e)}
                    imgList={[
                      result?.url?.startsWith('http')
                        ? result.url
                        : `data:image/png;base64,${result?.url}`,
                      currentImage?.pdfImgList?.[0]?.ImageUrl ??
                        currentImage?.url,
                    ]}
                    tips={currentMenu?.imageResultTips || ["增强后", "原图"]}
                  />
                </>
              )}
            </>
          </div>
          <div className="ocrDemo-imgListType-container">
            <div
              className={`ocrDemo-imgListType-item ${imgListType === "default" ? "active" : ""}`}
              onClick={() => setImgListType("default")}
            >
              示例文件
            </div>
            <div className="ocrDemo-imgListType-slice"></div>
            <div
              className={`ocrDemo-imgListType-item ${imgListType === "upload" ? "active" : ""}`}
              onClick={() => setImgListType("upload")}
            >
              上传文件
            </div>
            {imgListType === "upload" &&
              !isMultiUpload &&
              uploadImgList.length > 0 && (
                <div
                  className={`ocrDemo-imgListType-del ${uploadImgList?.length > 0 ? "" : "disable"}`}
                  onClick={() => clearUploadImgList()}
                >
                  <span className="ocrDemo-icon ocrDemo-icon__del"></span>
                  <span>清除所有上传</span>
                </div>
              )}
            {imgListType === "upload" &&
              isMultiUpload &&
              (multiUploadPages.length > 0 ||
                archivedBatches.length > 0) && (
                <div
                  className="ocrDemo-imgListType-del"
                  onClick={() => {
                    // 当前批次 + 已归档的历史批次一起清掉
                    clearMultiUploadPages();
                    clearUploadImgList();
                  }}
                >
                  <span className="ocrDemo-icon ocrDemo-icon__del"></span>
                  <span>清除所有上传</span>
                </div>
              )}
          </div>
          <div className="imgList_scrollView-container">
            <div className="imgList_scrollView" ref={domRef}>
              <div className="unv-thumbnail-list" ref={thumbnailRef}>
                {imgListType === "upload" && isMultiUpload && (
                  <div
                    className="ocrDemo-icon_uploadEmpty"
                    onClick={() => {
                      uploadRef.current?.onUpload();
                    }}
                  ></div>
                )}
                {/* 历史批次：每次上传的独立入参，点击可切回该批次查看结果 */}
                {imgListType === "upload" &&
                  isMultiUpload &&
                  archivedBatches.map(({ item, index }) => (
                    <div
                      key={item.uid || item.url || `batch-${index}`}
                      className="ocrDemo-imgItem-container uploadList"
                      onClick={() => handleImageClick(item, true)}
                    >
                      <Thumbnail
                        fileType={
                          item.pdfImgList?.[0]?.ImageUrl
                            ? undefined
                            : item.fileType
                        }
                        active={
                          item.uid
                            ? currentImage?.uid === item.uid
                            : currentImage?.url === item.url
                        }
                        imageUrl={
                          item.pdfImgList?.[0]?.ImageUrl ||
                          item.previewUrl ||
                          item.url
                        }
                      />
                      <div className="ocrDemo-multiPage-badge">
                        {item.multiPages.length} 页
                      </div>
                      <div className="ocrDemo-imgItem__delete">
                        <div
                          className="ocrDemo-icon_delete"
                          onClick={(e) => handleBatchDelete(e, index)}
                        ></div>
                      </div>
                    </div>
                  ))}
                {/* 当前批次：与历史批次统一为「照片组」卡片（首次多选也是照片组） */}
                {imgListType === "upload" &&
                  isMultiUpload &&
                  multiUploadPages.length > 0 && (
                    <div
                      className="ocrDemo-imgItem-container uploadList"
                      onClick={handleCurrentBatchClick}
                    >
                      <Thumbnail
                        fileType={
                          currentBatchPreviewImg
                            ? undefined
                            : currentBatchFirstPage?.fileType
                        }
                        active={isCurrentBatchActive}
                        imageUrl={currentBatchPreviewImg}
                      />
                      <div className="ocrDemo-multiPage-badge">
                        {multiUploadPages.length} 页
                      </div>
                      <div className="ocrDemo-imgItem__delete">
                        <div
                          className="ocrDemo-icon_delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearMultiUploadPages();
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                  {imgListType === "upload" && !isMultiUpload && (
                    <>
                      <div
                        className="ocrDemo-icon_uploadEmpty"
                        onClick={() => {
                          uploadRef.current.onUpload();
                        }}
                      ></div>
                      {uploadImgList?.map((i, index) => (
                        <div
                          key={i.url || index}
                          className="ocrDemo-imgItem-container uploadList"
                          onClick={() => handleImageClick(i, true)}
                        >
                          <Thumbnail
                            fileType={i.fileType}
                            active={
                              currentImage?.url === i.url ||
                              currentImage?.OriginUrl === i.OriginUrl
                            }
                            imageUrl={i.previewUrl || i.url}
                          />
                          <div className="ocrDemo-imgItem__delete">
                            <div
                              className="ocrDemo-icon_delete"
                              onClick={(e) => handleDeleteUploadImage(e, index)}
                            ></div>
                          </div>
                          {i.fileType && (
                            <div
                              className={`ocrDemo-imgItem__fileType ${imgFileType.includes(i.fileType) ? "img" : "file"}`}
                            >
                              {i.fileType}
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                  {imgListType === "default" &&
                    imgList?.map((i, index) => (
                      <div
                        key={i.url || index}
                        className="ocrDemo-imgItem-container"
                        onClick={() => handleImageClick(i)}
                      >
                        <Thumbnail
                          fileType={i.fileType}
                          previewImg={i.previewImg}
                          active={
                            currentImage?.url === i.url ||
                            currentImage?.OriginUrl === i.OriginUrl
                          }
                          imageUrl={i.previewUrl || i.url}
                        />
                        {i.fileType && (
                          <div
                            className={`ocrDemo-imgItem__fileType ${imgFileType.includes(i.fileType) ? "img" : "file"}`}
                          >
                            {i.fileType}
                          </div>
                        )}
                      </div>
                    ))}
              </div>
            </div>
            <div
              className={`${showThumListBtn ? "isActive" : "isHide"} imgList_scrollView__btn`}
              onClick={() => scrollTo(200)}
            ></div>
          </div>
          {isMultiUpload ? (
            <MultiUploadDropzone
              cRef={uploadRef}
              onBatchReady={() => {
                // 上传完成后自动发起识别（与单文件上传保持一致）。
                // 延后一拍，确保 store 里的 currentImage 已带上这一批的完整页
                setTimeout(() => {
                  const cur = useImageStore.getState().currentImage;
                  if (!cur?.multiPages?.length) return;
                  setImgListType("upload");
                  GeneralBasicOCRRef?.current?.start({ image: cur });
                }, 0);
              }}
            />
          ) : (
            <OcrUpload
              cRef={uploadRef}
              onSuccess={({ img }) => {
                setImgListType("upload");
                setIsImageError(true);
                // 先切到这张图并同步预览，避免识别结果返回前仍停留在示例图
                setCurrentImage(img);
                imgPreRef?.current?.onPreChange({
                  curImg: img.previewUrl || img.url,
                  curpPdfList: [],
                  imgSize: null,
                });
                GeneralBasicOCRRef?.current?.start({ image: img });
              }}
            />
          )}
        </div>
        <div
          style={{
            background: "#F4F5F7",
            width: "25%",
            maxWidth: "25%",
            minWidth: "320px",
            padding: "20px",
            flex: 1,
          }}
        >
          <GeneralBasicOCR
            onSelect={(_, rectVal) => {
              setSelectRectKey(rectVal);
              imgPreRef?.current?.onRectChange(rectVal);
            }}
            cRef={GeneralBasicOCRRef}
            onSubmitResult={({ image: submitImage, pdfPages }) => {
              if (!pdfPages?.length) return;
              // 抽题进行中：Submit 响应已带回 PDF 逐页图，
              // 立刻透出给左侧预览，避免整个轮询期间只能显示「无法预览」占位
              imgPreRef?.current?.onPreChange({
                curImg: pdfPages[0].ImageUrl,
                curpPdfList: pdfPages,
                imgSize: null,
              });
              setCurrentImage({
                ...submitImage,
                pdfImgList: pdfPages,
                previewUrl: pdfPages[0].ImageUrl,
                cantPreview: false,
                cantPreviewMsg: '',
              });
            }}
            onResult={({
              isError = false,
              image,
              rsp,
              requestParams,
              config = currentMenu,
              rect,
            }) => {
              setIsLoading(true);
              const _config = config || currentMenu;
              const pdfList =
                rsp.auditData?.length > 0 && rsp.auditData[0].ImageUrl
                  ? rsp.auditData
                  : [];

              // 结果所属图片已不是当前展示的图片时（典型场景：进入菜单时自动发起的
              // 示例图识别请求，晚于用户切换到上传图片才返回），不能再用它覆盖当前预览
              // 必须用 store 的实时值判断：onResult 走的是 handleStart 的旧闭包，
              // 闭包里的 currentImage 可能是切图之前的值，会把当前结果误判为过期结果
              const liveCurrentImageUrl = useImageStore.getState().currentImage?.url;
              const isStaleImageResult = Boolean(
                image?.url &&
                  liveCurrentImageUrl &&
                  image.url !== liveCurrentImageUrl
              );

              // 处理角度设置
              if (
                [0, 90, 180, 270, 360].includes(customRound(rsp?.Angle)) &&
                pdfList.length <= 0 &&
                currentMenu?.supportAngle
              ) {
                setAngle(customRound(rsp.Angle));
              } else {
                setAngle(0);
              }

              setTotalPage(rsp?.auditData?.length || 1);

              // 处理QuestionOCR相关逻辑
              const handleQuestionOCR = () => {
                let previewUrl =
                  pdfList.length > 0 ? pdfList[0].ImageUrl : image.url;
                let imgSize = null;

                if (rsp && rsp?.QuestionInfo?.length > 0) {
                  const qAngle = customRound(rsp?.QuestionInfo[0]?.Angle || 0);
                  const isRotated90or270 = qAngle === 90 || qAngle === 270;
                  const hasBase64 = !!rsp?.QuestionInfo[0]?.ImageBase64;

                  if (hasBase64) {
                    // Crop correction enabled: server returns a rotated/corrected base64 image.
                    // Swap width/height when angle is 90/270 to match the corrected image dimensions.
                    imgSize = {
                      width: isRotated90or270
                        ? rsp?.QuestionInfo[0].Height
                        : rsp?.QuestionInfo[0].Width,
                      height: isRotated90or270
                        ? rsp?.QuestionInfo[0].Width
                        : rsp?.QuestionInfo[0].Height,
                    };
                    previewUrl =
                      `data:image/png;base64,` +
                      rsp.QuestionInfo[0].ImageBase64;
                  } else {
                    // Crop correction disabled: no base64 returned.
                    // Frontend strips EXIF so the image renders in its original orientation.
                    // Use the API's original width/height directly — no swap needed.
                    imgSize = {
                      width: rsp?.QuestionInfo[0].Width,
                      height: rsp?.QuestionInfo[0].Height,
                    };
                  }
                }

                // 多图批次：一次上传多张时要按页展示整批（与 handleOtherOCR 保持一致）
                const isMultiBatch =
                  image?.isMultiUpload && image?.pdfImgList?.length > 0;

                // URL 在线 PDF 无法本地渲染（外链跨域 fetch 被拦），改用后端逐页返回的
                // ReturnImageUrl 作为预览底图：图片「显示」跨域不受 CORS 限制，
                // 只要不读像素（不裁剪 / 不 stripExif）即可。
                const returnImagePages = (rsp?.QuestionInfo || [])
                  .map((info) => ({
                    ImageUrl: info?.ReturnImageUrl,
                    Width: info?.Width,
                    Height: info?.Height,
                  }))
                  .filter((p) => !!p.ImageUrl);

                const effectivePdfList = isMultiBatch
                  ? image.pdfImgList.map((p, i) => {
                      const info = rsp?.QuestionInfo?.[i];
                      return {
                        ...p,
                        Height: info?.Height || p.Height,
                        Width: info?.Width || p.Width,
                      };
                    })
                  : pdfList.length > 0
                    ? pdfList
                    : returnImagePages;

                // 用后端跨域图当底图时不能 stripExif：stripExifFromUrl 会 fetch 这张
                // 跨域图而失败（回退原图、并产生报错噪音），直接跳过
                const usingCrossOriginReturnImage =
                  !isMultiBatch &&
                  pdfList.length === 0 &&
                  returnImagePages.length > 0;

                const _curImg = {
                  ...image,
                  cantPreview: false,
                  pdfImgList: effectivePdfList,
                  OriginUrl: image.url,
                  url: image.url,
                  previewUrl:
                    effectivePdfList.length > 0
                      ? effectivePdfList[0].ImageUrl ||
                        effectivePdfList[0].previewUrl
                      : previewUrl,
                };

                // 多图批次由 multiUploadPages 承载，归档时才进 uploadImgList，这里不能重复加入
                updateCurrentImage(_curImg, !_curImg.isExample && !_curImg.isMultiUpload);
                if (!isStaleImageResult) {
                  imgPreRef?.current?.onPreChange({
                    curImg: _curImg.previewUrl || previewUrl,
                    curpPdfList: effectivePdfList,
                    imgSize: imgSize,
                    stripExif: !usingCrossOriginReturnImage,
                    // 多页 PDF 才按「每页各自尺寸」换算坐标，单图/单页保持原逻辑不受影响
                    perPageSize: effectivePdfList.length > 1,
                  });
                }
              };

              // 处理其他OCR逻辑
              const handleOtherOCR = () => {
                let imgSize = null;
                if (rsp && rsp?.QuestionInfo?.length > 0) {
                  // Frontend strips EXIF so the image renders in its original orientation.
                  // Use the API's original width/height directly — no swap needed.
                  imgSize = {
                    width: rsp?.QuestionInfo[0].Width,
                    height: rsp?.QuestionInfo[0].Height,
                  };
                }

                // 多图上传批次：上传时已保存多页数据
                const isMultiBatch =
                  image?.isMultiUpload && image?.pdfImgList?.length > 0;

                // 多图批次：把每一页在后端坐标系里的宽高补上，
                // 预览层用 imgRadio = 首页高 / 本页高 来换算坐标框，缺了会导致第 2 页起框位偏移
                const enrichedMultiPages =
                  image?.pdfImgList?.map((p, i) => {
                    const info = rsp?.QuestionInfo?.[i];
                    return {
                      ...p,
                      Height: info?.Height || p.Height,
                      Width: info?.Width || p.Width,
                    };
                  }) || [];

                // 预览分页列表：
                // - 多图上传批次：始终用上传时保存的整批页图，保证「几张图就显示几页」
                //   （后端返回的 auditData 只覆盖 PDF 拆页，混批时会丢掉上传的图片）
                // - 其它场景：后端返回的分页图优先
                // - 兜底：PDF 本地渲染是异步的，识别发起时 image.pdfImgList 可能还空
                //   （线上渲染慢时必现），此时回退到 store 里实时的 pdfPreviewPages，
                //   否则会把预览 effect 刚设好的 cantPreview=false 覆盖回 true，卡在「正在解析」
                const livePdfPreviewPages =
                  useImageStore.getState().pdfPreviewPages || [];

                // URL 在线 PDF 无法本地渲染（外链跨域 fetch 被拦），改用后端逐页返回的
                // ReturnImageUrl 作为预览底图：图片「显示」跨域不受 CORS 限制，
                // 只要不读像素（不裁剪 / 不 stripExif）即可。
                const returnImagePages = (rsp?.QuestionInfo || [])
                  .map((info) => ({
                    ImageUrl: info?.ReturnImageUrl,
                    Width: info?.Width,
                    Height: info?.Height,
                  }))
                  .filter((p) => !!p.ImageUrl);

                const effectivePdfList =
                  isMultiBatch && enrichedMultiPages.length > 0
                    ? enrichedMultiPages
                    : pdfList.length > 0
                      ? pdfList
                      : image.fileType === 'pdf' && livePdfPreviewPages.length > 0
                        ? // 坐标换算基准(imgRadio = naturalHeight / Height)必须用后端坐标系
                          // 尺寸；本地渲染页自带的是渲染尺寸(如 1240×877)，直接用会让 imgRadio
                          // 偏成 1000/877≈1.14 导致坐标整体放大偏移，故用 QuestionInfo 覆盖
                          livePdfPreviewPages.map((p, i) => ({
                            ...p,
                            Width: rsp?.QuestionInfo?.[i]?.Width || p.Width,
                            Height: rsp?.QuestionInfo?.[i]?.Height || p.Height,
                          }))
                        : returnImagePages;

                // 用后端跨域图当底图时不能 stripExif：stripExifFromUrl 会 fetch 这张
                // 跨域图而失败（回退原图、并产生报错噪音），直接跳过
                const usingCrossOriginReturnImage =
                  !(isMultiBatch && enrichedMultiPages.length > 0) &&
                  pdfList.length === 0 &&
                  !(image.fileType === 'pdf' && livePdfPreviewPages.length > 0) &&
                  returnImagePages.length > 0;

                // PDF 文件且没有可预览的分页图时，无法作为 img 预览
                const isPdfWithoutPreview =
                  image.fileType === 'pdf' && effectivePdfList.length <= 0;

                if (!isPdfWithoutPreview && !isStaleImageResult) {
                  imgPreRef?.current?.onPreChange({
                    // URL-PDF 用 ReturnImageUrl 当底图时，image.url 是外链 PDF 不能当图显示，
                    // 取首页图片地址
                    curImg:
                      effectivePdfList.length > 0
                        ? effectivePdfList[0].ImageUrl ||
                          effectivePdfList[0].previewUrl
                        : image.previewUrl || image.url,
                    curpPdfList: effectivePdfList,
                    imgSize: imgSize,
                    stripExif: !usingCrossOriginReturnImage,
                    // 多页 PDF 才按「每页各自尺寸」换算坐标，单图/单页保持原逻辑不受影响
                    perPageSize: effectivePdfList.length > 1,
                  });
                }

                const _curImg = {
                  ...image,
                  pdfImgList: effectivePdfList,
                  previewUrl:
                    effectivePdfList.length > 0
                      ? effectivePdfList[0].ImageUrl ||
                        effectivePdfList[0].previewUrl
                      : image.previewUrl || image.url,
                  cantPreview: isPdfWithoutPreview,
                };

                updateCurrentImage(_curImg, !_curImg.isExample && !_curImg.isMultiUpload);
              };

              // 更新当前图片和上传列表
              const updateCurrentImage = (_curImg, shouldUpdateUploadList) => {
                // 过期结果（已切换到别的图片）只入上传列表，不覆盖当前预览
                if (isStaleImageResult) {
                  if (shouldUpdateUploadList) {
                    addUploadImage(_curImg);
                  }
                  return;
                }
                setCurrentImage(_curImg);
                if (shouldUpdateUploadList) {
                  addUploadImage(_curImg);
                }
              };

              // 处理矩形框数据
              const handleRectData = () => {
                if (rect?.length > 0) {
                  return rect;
                }

                let _rects = [];
                const isWordListOnly = _config?.rectConfig?.showWordListOnly;
                const showWordList =
                  _config?.rectConfig?.useWordList && rsp?.WordList?.length > 0;

                if (isWordListOnly) {
                  _rects = processRectConfig(
                    _config,
                    rsp,
                    pdfList,
                    requestParams,
                    "WordListRectConfig",
                  );
                } else {
                  if (showWordList) {
                    _rects = processRectConfig(
                      _config,
                      rsp,
                      pdfList,
                      requestParams,
                      "WordListRectConfig",
                    );
                  }
                  if (showWordList || !isWordListOnly) {
                    const additionalRects = processRectConfig(
                      _config,
                      rsp,
                      pdfList,
                      requestParams,
                      "rectConfig",
                    );
                    _rects.push(...additionalRects);
                  }
                }

                return _rects;
              };

              // 处理矩形配置
              const processRectConfig = (
                config,
                data,
                pdfListData,
                reqParams,
                configType,
              ) => {
                const resultRects = [];
                const configData = config[configType];

                if (Array.isArray(configData)) {
                  configData.forEach((configItem) => {
                    const rectResult = getRectPosition({
                      data: data,
                      config: configItem,
                      isMulPage: pdfListData.length > 1,
                      total: pdfListData.length,
                      PdfPageNumber: reqParams?.PdfPageNumber || 1,
                    });
                    resultRects.push(...rectResult);
                  });
                } else if (configData) {
                  const rectResult = getRectPosition({
                    data: data,
                    config: configData,
                    isMulPage: pdfListData.length > 1,
                    total: pdfListData.length,
                    PdfPageNumber: reqParams?.PdfPageNumber || 1,
                  });
                  resultRects.push(...rectResult);
                }
                return resultRects;
              };

              // 主逻辑分发
              if (
                [
                  "QuestionOCR",
                  "QuestionSplitOCR",
                  "QuestionSplitLayoutOCR",
                  "SubmitQuestionMarkAgentJob",
                ].includes(_config.action) ||
                _config.actionType === "markEdu"
              ) {
                handleQuestionOCR();
              } else {
                handleOtherOCR();
              }

              if (!isError && !isStaleImageResult) {
                setTimeout(() => {
                  const _rects = handleRectData();
                  setRectDocuments(_rects);
                  setShowImgList(false);
                }, 0);
              }

              setIsLoading(false);
            }}
            onImageError={(res) => {
              setIsImageError(res);
              if (res) {
                setCurrentImage("");
              }
            }}
            onChangeImg={(img, params) => {
              const _curImg = {
                ...currentImage,
                url: img,
                previewUrl: img,
                OriginUrl: img,
              };
              setCurrentImage(_curImg);
              GeneralBasicOCRRef?.current?.start({
                image: _curImg,
                config: params,
              });
            }}
            onChangeImageEnhancement={(res) => {
              setResult({
                url: res.url,
              });
              setImgPreType(res.type);
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default OcrLayout;
